import { Divider } from 'antd'
import { useCallback, useState } from 'react'

import { Loading } from '@/components'

import { ComputeNodeContent } from '../components/ComputeNodeContent'
import { PrivateRegionViewSwitcher } from '../components/PrivateRegionViewSwitcher'
import { RegionSelector } from '../components/RegionSelector'
import { useComputeNodeData, useDashboardData, useDomainSelection, useUsageMetrics } from '../hooks'

export function ComputeView() {
  const [filters, setFilters] = useState<{ gpuModel?: string | number }>({})

  // Domain selection
  const {
    domainInfos,
    selectedRegion,
    setSelectedRegion,
    loading: domainLoading,
  } = useDomainSelection()

  // Dashboard data (for overview cards)
  const {
    overviewData,
    loading: dashboardLoading,
    refetch: dashboardRefetch,
  } = useDashboardData(selectedRegion, domainInfos, filters.gpuModel)

  // Usage metrics (for overview cards)
  const { usageMetrics } = useUsageMetrics(selectedRegion, filters.gpuModel)

  // Compute node data
  const {
    computeNodes,
    models,
    page,
    setPage,
    loading: computeLoading,
    pageSize,
    refetch: computeRefetch,
  } = useComputeNodeData(selectedRegion, filters)

  // Only show full-page loading on initial load (no data yet)
  const initialLoading =
    domainLoading || (dashboardLoading && !overviewData) || (computeLoading && !computeNodes)

  // Combined refetch: refresh both compute nodes and dashboard overview cards
  const handleRefetch = useCallback(() => {
    computeRefetch()
    dashboardRefetch()
  }, [computeRefetch, dashboardRefetch])

  return (
    <div className="card bg-panel-white h-full overflow-y-auto">
      <div className="">
        <div className="flex items-center justify-between">
          <PrivateRegionViewSwitcher />
          <RegionSelector
            domainInfos={domainInfos}
            selectedRegion={selectedRegion}
            setSelectedRegion={setSelectedRegion}
          />
        </div>

        <Divider className="my-8" />

        {/* Content */}
        {initialLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loading />
          </div>
        ) : (
          <ComputeNodeContent
            computeNodes={computeNodes}
            models={models}
            page={page}
            pageSize={pageSize}
            filters={filters}
            onPageChange={setPage}
            onFiltersChange={setFilters}
            overviewData={overviewData}
            usageMetrics={usageMetrics}
            onRefetch={handleRefetch}
          />
        )}
      </div>
    </div>
  )
}
